// Tableau de saisie de l'équipe d'un chef de pôle : une ligne par personne (nom, prénom, email, type
// d'accès, champs personnalisés, jours, cashless dépliable). On peut COLLER des lignes copiées depuis
// Excel dans une case de texte. Composant d'affichage : l'état des lignes, le collage et les
// validations vivent dans PoleTeamForm / poleTeamModel (le serveur revérifie tout).
import React from 'react';
import { Box, Stack, Typography, Chip } from '@mui/material';
import { Plus, Copy, Trash2, ChevronDown, ChevronUp, Pencil } from 'lucide-react';
import {
  CASHLESS_FIELDS, isEmptyRow, rowProblems, isGridPaste, parsePaste, effectiveCashless, summarizeCashless, rulesToCells,
} from './poleTeamModel';

const TODO = '#e8a27a'; // champ requis manquant (après une tentative d'envoi)
const INVALID = '#e57373'; // valeur invalide (email, cashless)

const fieldSx = (border = '#dde3e8') => ({
  width: '100%', minWidth: 0, height: 34, px: 1.25, fontFamily: 'inherit', fontSize: 13, color: '#002F36',
  bgcolor: '#fff', border: `1px solid ${border}`, borderRadius: '6px', outline: 'none', boxSizing: 'border-box',
  '&:focus': { borderColor: '#00B797', boxShadow: '0 0 0 3px #00b79726' },
  '&::placeholder': { color: '#9aa3aa' },
});
const selectSx = (border, filled) => ({ ...fieldSx(border), px: 0.75, color: filled ? '#002F36' : '#9aa3aa' });

const formatDayDate = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '';
};

const gridColumns = (extraCount, hasDays, canCashless) => [
  '34px', 'minmax(110px, 1fr)', 'minmax(110px, 1fr)', 'minmax(150px, 1.4fr)', 'minmax(150px, 1.4fr)',
  ...Array(extraCount).fill('minmax(100px, 0.9fr)'),
  hasDays ? 'minmax(170px, 1.2fr)' : null,
  canCashless ? '150px' : null,
  '64px',
].filter(Boolean).join(' ');

const PoleTeamTable = ({
  people, config, accessTypes, typeRules, canCashless, hasDays, tried, emailIssues, openId,
  onToggleOpen, onPatch, onPatchCustom, onPatchCashless, onResetCashless, onToggleDay,
  onDuplicate, onRemove, onAddRow, onPasteRows,
}) => {
  const customFields = config?.customFields || [];
  const template = gridColumns(customFields.length, hasDays, canCashless);

  const handlePaste = (e, idx, columnId) => {
    const raw = e.clipboardData.getData('text');
    if (!isGridPaste(raw)) return;
    e.preventDefault();
    onPasteRows(idx, columnId, parsePaste(raw));
  };

  return (
    <Box sx={{
      flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#fff',
      border: '1px solid #dde3e8', borderRadius: '12px', overflow: 'hidden',
    }}>
      <Box sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ minWidth: 'max-content' }}>
          <Box sx={{
            position: 'sticky', top: 0, zIndex: 1, display: 'grid', gridTemplateColumns: template, gap: 1, alignItems: 'center',
            px: 1.75, py: 1.25, fontSize: 12, fontWeight: 600, color: '#5e646a', bgcolor: '#f8fafb', borderBottom: '1px solid #e9edf1',
          }}>
            <span>#</span><span>Nom *</span><span>Prénom *</span><span>Email</span><span>Type d'accès *</span>
            {customFields.map((f) => <span key={f.key}>{f.required ? `${f.label} *` : f.label}</span>)}
            {hasDays && <span>Jours</span>}
            {canCashless && <span>Cashless</span>}
            <span />
          </Box>

          {people.map((p, idx) => {
            const empty = isEmptyRow(p);
            const problems = empty ? [] : rowProblems(p, { customFields, emailIssue: emailIssues[idx] });
            const done = !empty && problems.length === 0;
            const todo = (id) => (tried && problems.includes(id) ? TODO : '#dde3e8');
            const open = openId === p.id;
            const eff = effectiveCashless(p, typeRules);
            const rowCells = rulesToCells(typeRules?.[p.accessTypeKey]);
            const n = idx + 1;

            return (
              <Box key={p.id} sx={{ borderBottom: '1px solid #eef1f4', bgcolor: open ? '#fbfdfc' : '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: template, gap: 1, alignItems: 'center', px: 1.75, py: 0.75 }}>
                  <Box sx={{
                    width: 26, height: 26, display: 'grid', placeItems: 'center', borderRadius: '7px', fontSize: 12, fontWeight: 700,
                    bgcolor: done ? '#d0f4e3' : empty ? '#f1f4f6' : '#fdf0e3', color: done ? '#007739' : empty ? '#9aa3aa' : '#b54708',
                  }}>
                    {n}
                  </Box>

                  <Box
                    component="input" aria-label={`Nom, ligne ${n}`} placeholder="Nom" value={p.nom}
                    onChange={(e) => onPatch(idx, { nom: e.target.value })} onPaste={(e) => handlePaste(e, idx, 'nom')}
                    sx={fieldSx(todo('nom'))}
                  />
                  <Box
                    component="input" aria-label={`Prénom, ligne ${n}`} placeholder="Prénom" value={p.prenom}
                    onChange={(e) => onPatch(idx, { prenom: e.target.value })} onPaste={(e) => handlePaste(e, idx, 'prenom')}
                    sx={fieldSx(todo('prenom'))}
                  />
                  <Box
                    component="input" type="email" aria-label={`Email, ligne ${n}`} placeholder="email@exemple.fr" value={p.email}
                    title={emailIssues[idx] || ''}
                    onChange={(e) => onPatch(idx, { email: e.target.value })} onPaste={(e) => handlePaste(e, idx, 'email')}
                    sx={fieldSx(emailIssues[idx] ? INVALID : '#dde3e8')}
                  />
                  <Box
                    component="select" aria-label={`Type d'accès, ligne ${n}`} value={p.accessTypeKey || ''}
                    title={accessTypes.find((t) => t.key === p.accessTypeKey)?.label || ''}
                    onChange={(e) => onPatch(idx, { accessTypeKey: e.target.value })}
                    disabled={accessTypes.length === 0} sx={selectSx(todo('type'), Boolean(p.accessTypeKey))}
                  >
                    <option value="">Choisir…</option>
                    {accessTypes.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </Box>

                  {customFields.map((f) => {
                    const value = p.customValues?.[f.key] || '';
                    const border = todo(`custom.${f.key}`);
                    return f.type === 'select' ? (
                      <Box
                        key={f.key} component="select" aria-label={`${f.label}, ligne ${n}`} value={value}
                        onChange={(e) => onPatchCustom(idx, f.key, e.target.value)} sx={selectSx(border, Boolean(value))}
                      >
                        <option value="">—</option>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </Box>
                    ) : (
                      <Box
                        key={f.key} component="input" aria-label={`${f.label}, ligne ${n}`} value={value}
                        onChange={(e) => onPatchCustom(idx, f.key, e.target.value)}
                        onPaste={(e) => handlePaste(e, idx, `custom.${f.key}`)} sx={fieldSx(border)}
                      />
                    );
                  })}

                  {hasDays && (
                    <Stack direction="row" spacing={0.5}>
                      {config.eventDays.map((d) => {
                        const on = p.presentDays.includes(d.label);
                        return (
                          <Chip
                            key={d.label} size="small" clickable color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                            onClick={() => onToggleDay(idx, d.label)}
                            label={formatDayDate(d.date) ? `${d.label} · ${formatDayDate(d.date)}` : d.label}
                          />
                        );
                      })}
                    </Stack>
                  )}

                  {canCashless && (
                    <Box
                      component="button" type="button" onClick={() => onToggleOpen(p.id)}
                      aria-expanded={open} aria-label={`Cashless, ligne ${n}`}
                      title={eff.overridden ? 'Valeurs personnalisées' : 'Règle du type — cliquer pour personnaliser'}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', minWidth: 0, height: 34, px: 1.25,
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 600, textAlign: 'left', cursor: 'pointer', borderRadius: '6px',
                        color: eff.overridden ? '#007a66' : eff.hasRule ? '#002F36' : '#9aa3aa',
                        bgcolor: eff.overridden ? '#e6f8f3' : '#f8fafb',
                        border: `1px solid ${open ? '#00B797' : eff.overridden ? '#a6e6d6' : '#e3e8ec'}`,
                        '&:hover': { borderColor: '#00B797' },
                      }}
                    >
                      <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {!eff.hasRule && !eff.overridden ? 'Selon le type' : summarizeCashless(eff.values)}
                      </Box>
                      {eff.overridden ? <Pencil size={11} /> : open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                    </Box>
                  )}

                  <Stack direction="row" spacing={0.25}>
                    <Box
                      component="button" type="button" title="Dupliquer" aria-label={`Dupliquer la ligne ${n}`} onClick={() => onDuplicate(idx)}
                      sx={{ width: 30, height: 30, display: 'grid', placeItems: 'center', color: '#5e646a', bgcolor: 'transparent', border: 0, borderRadius: '6px', cursor: 'pointer', '&:hover': { bgcolor: '#eef3f6', color: '#002F36' } }}
                    >
                      <Copy size={13} />
                    </Box>
                    <Box
                      component="button" type="button" title="Retirer" aria-label={`Retirer la ligne ${n}`} onClick={() => onRemove(idx)}
                      sx={{ width: 30, height: 30, display: 'grid', placeItems: 'center', color: '#5e646a', bgcolor: 'transparent', border: 0, borderRadius: '6px', cursor: 'pointer', '&:hover': { bgcolor: '#fdecea', color: '#b42318' } }}
                    >
                      <Trash2 size={13} />
                    </Box>
                  </Stack>
                </Box>

                {canCashless && open && (
                  <Stack
                    direction="row" alignItems="flex-end" flexWrap="wrap" useFlexGap spacing={0}
                    sx={{ gap: '10px 16px', mx: 1.75, mb: 1.25, ml: 7, p: '10px 12px', bgcolor: '#f2fbf8', border: '1px solid #c9eee4', borderRadius: '8px' }}
                  >
                    <Box sx={{ flex: '1 1 220px' }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Cashless personnalisé</Typography>
                      <Typography sx={{ fontSize: 12, color: '#5e646a' }}>
                        {eff.hasRule
                          ? `Règle du type : ${summarizeCashless(rowCells)}. Laissez vide pour garder la valeur de la règle.`
                          : "Aucun type choisi : seules ces valeurs s'appliquent."}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 80px)', gap: 1 }}>
                      {CASHLESS_FIELDS.map((f) => {
                        const raw = String(p.cashless?.[f.key] ?? '').trim();
                        const invalid = raw !== '' && !(Number.isFinite(Number(raw)) && Number(raw) >= 0);
                        return (
                          <Box key={f.key} component="label" sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#5e646a' }}>{f.label}</Typography>
                            <Box
                              component="input" type="number" min={0} aria-label={`${f.label}, ligne ${n}`}
                              placeholder={rowCells[f.key] || '0'} value={p.cashless?.[f.key] ?? ''}
                              onChange={(e) => onPatchCashless(idx, f.key, e.target.value)}
                              sx={{ ...fieldSx(invalid ? INVALID : '#dde3e8'), height: 32 }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                    <Box
                      component="button" type="button" onClick={() => onResetCashless(idx)}
                      sx={{ height: 32, px: 1.25, fontFamily: 'inherit', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: '#5e646a', bgcolor: '#fff', border: '1px solid #dde3e8', borderRadius: '6px', cursor: 'pointer', '&:hover': { color: '#002F36', borderColor: '#002F36' } }}
                    >
                      Revenir à la règle
                    </Box>
                  </Stack>
                )}
              </Box>
            );
          })}

          <Box
            component="button" type="button" onClick={onAddRow}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', height: 46, pl: 7, pr: 1.75, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#00876f', bgcolor: 'transparent', border: 0, cursor: 'pointer', '&:hover': { bgcolor: '#f2fbf8' } }}
          >
            <Plus size={14} />Ajouter une personne
            <Box component="span" sx={{ fontWeight: 500, color: '#9aa3aa' }}>— ou collez des lignes Excel dans une case Nom</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PoleTeamTable;
